import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormlyFormOptions, FormlyFieldConfig } from '@ngx-formly/core';
import { PrimeNGConfig } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import clonedeep from 'lodash.clonedeep'

import { ClientDataService } from '../../services/client-data.service';
import { stateAnimation } from '../../api/animations';
import { ConfirmationDialogService, ConfirmationDialogResult } from '../../services/confirmation-dialog.service';
import { convertDateProperties } from '../../helpers/convert-date-properties';
import { getLeafNodesWithKeys } from '../../helpers/extract-props';
import { BASE_URL } from "@jwt";

export abstract class KendoEditFormToken {
    abstract canDeactivate(): Observable<boolean> | Promise<boolean> | boolean;
}
export interface FormlyFieldPropsEvent {
    properties: { [key: string]: FormlyFieldConfig };
    model: any;
    form: FormGroup;
}

@Component({
    template: ""
})
export class KendoBaseEditForm {
    @ViewChild(KendoEditFormToken) editForm: KendoEditFormToken | null = null;

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        return this.editForm.canDeactivate();
    }
}

@Component({
    selector: 'kendo-edit-form',
    templateUrl: './edit-form.component.html',
    providers: [{ provide: KendoEditFormToken, useExisting: KendoEditFormComponent }],
    animations: [stateAnimation]
})
export class KendoEditFormComponent extends KendoEditFormToken implements OnInit {
    @Input() animate: boolean = true;
    @Input() parentUrl: string;
    @Input() createUrl: string;
    @Input() editUrl: string;
    @Input() editParams: { [key: string]: any };
    @Input() createParams: { [key: string]: any };
    @Input() dropdownToolbarLabel: string;
    @Input() dropdownToolbarData: any[];

    @Output() formlyFieldPropsChange: EventEmitter<FormlyFieldPropsEvent> = new EventEmitter();

    form = new FormGroup({});
    options: FormlyFormOptions = {};
    fields: FormlyFieldConfig[];

    // Signals to hold and manage state
    entityId: number;
    model: any;
    originalModel: any;

    // Signal to track if the form has unsaved changes
    loading: boolean;
    pageTitle: string;
    saveLabel: string;
    saveAndExitLabel: string;
    cancelLabel: string;
    backLabel: string;
    borderForm: any;

    private isInitialized: boolean = false;

    get isDirty() {
        return JSON.stringify(this.model) !== JSON.stringify(this.originalModel);
    }

    constructor(
        @Inject(BASE_URL) private baseUrl: string,
        private router: Router,
        private route: ActivatedRoute,
        private primengConfig: PrimeNGConfig,
        private dataService: ClientDataService,
        private confirmService: ConfirmationDialogService,
        private toastrService: ToastrService
    ) {
        super();

        this.dropdownToolbarLabel = this.dropdownToolbarLabel || this.primengConfig.getTranslation('common.actions');
        this.saveLabel = this.primengConfig.getTranslation('common.save');
        this.saveAndExitLabel = this.primengConfig.getTranslation('common.saveAndExit');
        this.cancelLabel = this.primengConfig.getTranslation('common.cancel');
        this.backLabel = this.primengConfig.getTranslation('common.back');
    }

    ngOnInit(): void {
        // Set up entity ID from the route
        this.entityId = +this.route.snapshot.paramMap.get('id');

        // Fetch and initialize the form with entity data
        this.loadEntity();
    }

    loadEntity(): Promise<void> {
        const action = this.entityId === 0 ? this.createUrl : `${this.editUrl}`;
        const params = this.entityId === 0 ? this.createParams : { ...{ id: this.entityId }, ...this.editParams };
        const url = `${this.baseUrl}/${action}`;

        this.loading = true;

        return this.dataService.fetchJsonBodyGet(url, params)
            .then((result: any) => {
                convertDateProperties(result.model);
                this.originalModel = clonedeep(result.model);
                this.model = result.model;

                if (this.isInitialized) {
                    // Reset the form state to clean
                    this.form.markAsPristine();  // Mark the form as clean
                } else {
                    const properties = {
                        properties: getLeafNodesWithKeys(result.form.fields),
                        model: this.model,
                        form: this.form
                    };

                    this.formlyFieldPropsChange.emit(properties);
                    this.pageTitle = result.form.title;
                    this.fields = result.form.fields;
                    this.isInitialized = true;
                }
            })
            .catch((error: Error) => {
                Promise.reject(error);
                if (!this.isInitialized) this.goBack();
            }).finally(() => this.loading = false);
    }

    saveChanges(deactivating: boolean = false) {
        const action = this.entityId === 0 ? this.createUrl : this.editUrl;
        const url = `${this.baseUrl}/${action}`;

        this.loading = true;
        this.dataService.fetchJsonBodyPost(url, this.model)
            .then((result: any) => {

                this.entityId = result.id;

                if (!deactivating) {
                    // After saving, reload the entity from the server
                    this.loadEntity().then(() => {
                        // Show the real url and clear history
                        this.router.navigate(['../', this.entityId], { relativeTo: this.route, replaceUrl: true });
                    });
                } else {
                    this.originalModel = clonedeep(this.model);
                    this.goBack();
                }

                if (result.notify) {
                    if (result.notify.success === true) {
                        this.toastrService.success(result.notify.info);
                    } else {
                        this.toastrService.error(result.notify.errors, null, {
                            disableTimeOut: true,
                            positionClass: 'toast-top-center'
                        });
                    }
                } else {
                    this.toastrService.success(this.primengConfig.getTranslation("message.successfullySaved"));
                }

            })
            .catch((error: Error) => {
                Promise.reject(error);
            }).finally(() => this.loading = false);
    }

    cancelChanges(): void {
        // Revert the form model to the original state
        this.model = clonedeep(this.originalModel);
        this.form.markAsPristine();  // Reset form status
    }

    // Guard method to check if the form can be deactivated
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (!this.isDirty) return true;
        
        return this.confirmService.openDialog(
            this.primengConfig.getTranslation('common.warning'), this.primengConfig.getTranslation('message.canDeactivate'), false)
            .then((result) => {
                return result == ConfirmationDialogResult.Ok ? true : false;
            });
    }

    goBack() {
        this.router.navigate([this.parentUrl]);
    }
}
